import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { CreditCard, Landmark, Lock, Plus, Trash2, Star, Building2, Globe, Shuffle } from 'lucide-react';
import { getDoctorPaymentSettings, updateDoctorPaymentSettings, updateDoctorBankAccount } from '../../redux/paymentSlice';

const TIMING_LABELS = {
    postpaid: 'After the visit',
    prepaid: 'Before the visit',
    both: 'Either way',
};

const TIMING_DESC = {
    postpaid: 'Collected in person at the clinic',
    prepaid: 'Paid online in advance',
    both: 'Patient picks at booking',
};

const TIMING_ICONS = {
    postpaid: Building2,
    prepaid: Globe,
    both: Shuffle,
};

const newId = () => 'acc_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
const blankAccount = (isPrimary = false) => ({
    id: newId(), holderName: '', country: 'FR', iban: '', bic: '', accountNo: '', ifsc: '', isPrimary,
});

// Doctor self-serve: pick accepted methods (from admin-enabled), timing preference
// (within admin policy), and payout bank accounts. A bank account is only needed
// when the doctor accepts a non-cash ("transaction") method — cash-only doctors
// don't need one, so the bank card is disabled in that case.
export default function DoctorPaymentTab({ doctor }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const doctorId = doctor?.doctorId || localStorage.getItem('ID');
    const settings = useSelector((s) => s.payment?.doctorSettings);

    const [accepted, setAccepted] = useState([]);
    const [timing, setTiming] = useState('postpaid');
    const [bankAccounts, setBankAccounts] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (doctorId) dispatch(getDoctorPaymentSettings(doctorId)); }, [doctorId, dispatch]);

    useEffect(() => {
        if (settings) {
            setAccepted(settings.acceptedPaymentMethods || []);
            setTiming(settings.paymentTimingPreference || 'postpaid');
            const accts = Array.isArray(settings.bankAccounts) ? settings.bankAccounts : [];
            setBankAccounts(accts.map((a) => ({
                id: a.id || newId(),
                holderName: a.holderName || '',
                country: a.country || 'FR',
                iban: a.iban || '',
                bic: a.bic || '',
                accountNo: a.accountNo || '',
                ifsc: a.ifsc || '',
                isPrimary: !!a.isPrimary,
            })));
        }
    }, [settings]);

    const adminMethods = settings?.adminMethods || [];
    const adminPolicy = settings?.adminTimingPolicy || 'postpaid';
    const timingChoices = adminPolicy === 'both' ? ['postpaid', 'prepaid', 'both'] : [adminPolicy];

    // Whether the doctor's plan can accept ONLINE payments at all (backend gates this
    // by online_payment). Defaults to true if the field is absent (older backend).
    const onlineCapable = settings?.onlinePaymentAllowed !== false;
    // The payout bank account only matters for ONLINE payments — the platform collects
    // then pays out to the doctor. Cash + Card/Terminal are collected in person on the
    // doctor's own drawer/terminal, so they never need a payout account.
    const requiresBank = accepted.includes('online');

    // Seed one primary account the first time a transaction method is accepted.
    useEffect(() => {
        if (requiresBank && bankAccounts.length === 0) {
            setBankAccounts([blankAccount(true)]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requiresBank]);

    const toggleAccept = (key) => {
        setAccepted((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    };

    const addAccount = () => setBankAccounts((prev) => [...prev, blankAccount(prev.length === 0)]);
    const removeAccount = (id) => setBankAccounts((prev) => {
        const next = prev.filter((a) => a.id !== id);
        if (next.length && !next.some((a) => a.isPrimary)) next[0] = { ...next[0], isPrimary: true };
        return next;
    });
    const setPrimary = (id) => setBankAccounts((prev) => prev.map((a) => ({ ...a, isPrimary: a.id === id })));
    const updateAccount = (id, patch) => setBankAccounts((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));

    const handleSave = async () => {
        if (accepted.length === 0) {
            toast.warn(t('payments.validation_method_required', 'Select at least one payment method you accept'));
            return;
        }
        let toSend = bankAccounts;
        if (requiresBank) {
            if (bankAccounts.length === 0) {
                toast.warn(t('payments.validation_bank_required', 'Add at least one bank account for non-cash payments'));
                return;
            }
            for (const a of bankAccounts) {
                if (!a.holderName.trim()) {
                    toast.warn(t('payments.validation_holder_required', 'Account holder name is required'));
                    return;
                }
                if (a.country === 'IN' ? (!a.accountNo.trim() || !a.ifsc.trim()) : (!a.iban.trim() || !a.bic.trim())) {
                    toast.warn(t('payments.validation_bank_fields_required', 'Please complete all bank account fields'));
                    return;
                }
            }
            // Guarantee exactly one primary.
            if (!bankAccounts.some((a) => a.isPrimary)) {
                toSend = bankAccounts.map((a, i) => ({ ...a, isPrimary: i === 0 }));
            }
        }

        // Drop blank rows (e.g. a seeded-but-unused account when only cash is
        // selected) and strip to region-relevant fields before sending.
        let cleanAccounts = toSend
            .filter((a) => a.holderName.trim() || a.iban.trim() || a.accountNo.trim())
            .map((a) => (a.country === 'IN'
                ? { id: a.id, holderName: a.holderName, country: 'IN', accountNo: a.accountNo, ifsc: a.ifsc, isPrimary: a.isPrimary }
                : { id: a.id, holderName: a.holderName, country: 'FR', iban: a.iban, bic: a.bic, isPrimary: a.isPrimary }));
        // Ensure a primary survives the filtering.
        if (cleanAccounts.length && !cleanAccounts.some((a) => a.isPrimary)) {
            cleanAccounts = cleanAccounts.map((a, i) => ({ ...a, isPrimary: i === 0 }));
        }

        setSaving(true);
        try {
            await dispatch(updateDoctorPaymentSettings({
                doctorId,
                acceptedPaymentMethods: accepted,
                paymentTimingPreference: timing,
            })).unwrap();
            await dispatch(updateDoctorBankAccount({ doctorId, bankAccounts: cleanAccounts })).unwrap();
            toast.success(t('payments.saved', 'Payment settings saved'));
        } catch (err) {
            toast.warn(typeof err === 'string' ? err : t('payments.save_failed', 'Failed to save payment settings'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="doctor-payment-tab" className="space-y-4 sm:space-y-6 w-full">
            {adminMethods.length === 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    {t('payments.none_enabled', 'No payment methods are enabled by the admin yet.')}
                </div>
            )}

            {/* Top row: accepted methods + collection timing, side by side on large screens */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-stretch">
            {/* Accepted methods */}
            <div className={`rounded-2xl border border-gray-200 ${adminPolicy === 'both' ? '' : 'lg:col-span-2'}`}>
                <div className="flex items-center gap-2 p-4 border-b border-gray-100">
                    <CreditCard className="w-4 h-4 text-[#4ABDBD]" />
                    <h3 className="font-semibold text-gray-800">{t('payments.accepted_methods', 'Payment methods you accept')}</h3>
                </div>
                <div className="divide-y divide-gray-100">
                    {adminMethods.map((m) => {
                        const disabled = !!m.requiresGateway; // can't accept online methods until gateway is wired
                        return (
                            <label
                                key={m.key}
                                data-testid={`doctor-payment-method-${m.key}`}
                                className={`flex items-center justify-between gap-4 p-4 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
                            >
                                <span className="text-sm font-medium text-gray-800 flex items-center gap-2">
                                    {m.label}
                                    {disabled && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                            <Lock className="w-3 h-3" /> {t('payments.coming_soon', 'Coming soon')}
                                        </span>
                                    )}
                                </span>
                                <input
                                    type="checkbox"
                                    className="w-5 h-5 accent-[#4ABDBD]"
                                    checked={accepted.includes(m.key)}
                                    disabled={disabled}
                                    onChange={() => toggleAccept(m.key)}
                                    data-testid={`doctor-payment-check-${m.key}`}
                                />
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* Timing preference (only when admin allows a choice) */}
            {adminPolicy === 'both' && (
                <div className="rounded-2xl border border-gray-200 p-4 h-full flex flex-col">
                    <h3 className="font-semibold text-gray-800 mb-3">{t('payments.timing_preference', 'When do you collect payment?')}</h3>
                    <div className="flex flex-col gap-2.5 flex-grow">
                        {timingChoices.map((v) => {
                            const Icon = TIMING_ICONS[v];
                            const sel = timing === v;
                            return (
                                <button
                                    key={v}
                                    type="button"
                                    onClick={() => setTiming(v)}
                                    data-testid={`doctor-payment-timing-${v}`}
                                    className={`flex-1 flex items-center gap-3 text-left px-4 py-3 rounded-xl border transition ${sel ? 'border-[#4ABDBD] bg-[#4ABDBD]/10' : 'border-gray-200 hover:border-[#4ABDBD]/60'}`}
                                >
                                    <span className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 transition-colors ${sel ? 'bg-[#4ABDBD] text-white' : 'bg-gray-100 text-gray-500'}`}>
                                        <Icon className="w-4 h-4" />
                                    </span>
                                    <div className="min-w-0">
                                        <p className={`text-sm font-semibold ${sel ? 'text-[#3AA8A8]' : 'text-gray-700'}`}>
                                            {t(`payments.timing_${v}`, TIMING_LABELS[v])}
                                        </p>
                                        <p className="text-xs text-gray-400">{t(`payments.timing_desc_${v}`, TIMING_DESC[v])}</p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
            </div>

            {/* Bank accounts — only relevant when the plan can accept ONLINE payments
                (platform payout). Hidden entirely otherwise; required only once the
                doctor actually accepts 'online'. */}
            <div data-testid="doctor-bank-card" className={`rounded-2xl border p-4 space-y-3 transition ${!onlineCapable ? 'hidden' : ''} ${requiresBank ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-70'}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Landmark className="w-4 h-4 text-[#4ABDBD]" />
                        <h3 className="font-semibold text-gray-800">{t('payments.bank_accounts', 'Bank accounts (for payouts)')}</h3>
                        {requiresBank && <span className="text-xs font-semibold text-red-500">*</span>}
                    </div>
                    {requiresBank && (
                        <button
                            type="button"
                            onClick={addAccount}
                            data-testid="doctor-bank-add"
                            className="inline-flex items-center gap-1 text-sm font-semibold text-[#3AA8A8] hover:text-[#2D8A8A]"
                        >
                            <Plus className="w-4 h-4" /> {t('payments.add_account', 'Add account')}
                        </button>
                    )}
                </div>

                {!requiresBank ? (
                    <p data-testid="doctor-bank-not-required" className="text-sm text-gray-500">
                        {t('payments.bank_not_required', 'Only cash is selected — no bank account is needed.')}
                    </p>
                ) : (
                    <div className="space-y-4">
                        {bankAccounts.map((acc) => (
                            <div key={acc.id} data-testid={`doctor-bank-account-${acc.id}`} className="rounded-xl border border-gray-200 p-3 space-y-3 bg-white">
                                <div className="flex items-center justify-between">
                                    <button
                                        type="button"
                                        onClick={() => setPrimary(acc.id)}
                                        data-testid={`doctor-bank-primary-${acc.id}`}
                                        className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg border transition ${acc.isPrimary ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#3AA8A8]' : 'border-gray-200 text-gray-500 hover:border-[#4ABDBD]'}`}
                                    >
                                        <Star className={`w-3.5 h-3.5 ${acc.isPrimary ? 'fill-[#3AA8A8] text-[#3AA8A8]' : ''}`} />
                                        {acc.isPrimary ? t('payments.primary', 'Primary') : t('payments.set_primary', 'Set as primary')}
                                    </button>
                                    {bankAccounts.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeAccount(acc.id)}
                                            data-testid={`doctor-bank-remove-${acc.id}`}
                                            className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-600"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" /> {t('payments.remove', 'Remove')}
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    value={acc.holderName}
                                    onChange={(e) => updateAccount(acc.id, { holderName: e.target.value })}
                                    placeholder={t('payments.account_holder', 'Account holder name')}
                                    data-testid={`doctor-bank-holder-${acc.id}`}
                                    className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:border-[#4ABDBD] focus:ring-1 focus:ring-[#4ABDBD] outline-none"
                                />
                                <select
                                    value={acc.country}
                                    onChange={(e) => updateAccount(acc.id, { country: e.target.value })}
                                    data-testid={`doctor-bank-country-${acc.id}`}
                                    className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:border-[#4ABDBD] outline-none"
                                >
                                    <option value="FR">{t('payments.country_fr', 'France (IBAN / BIC)')}</option>
                                    <option value="IN">{t('payments.country_in', 'India (Account / IFSC)')}</option>
                                </select>
                                </div>
                                {acc.country === 'IN' ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <input type="text" value={acc.accountNo} onChange={(e) => updateAccount(acc.id, { accountNo: e.target.value })}
                                            placeholder={t('payments.account_no', 'Account number')} data-testid={`doctor-bank-accountno-${acc.id}`}
                                            className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:border-[#4ABDBD] outline-none" />
                                        <input type="text" value={acc.ifsc} onChange={(e) => updateAccount(acc.id, { ifsc: e.target.value })}
                                            placeholder={t('payments.ifsc', 'IFSC code')} data-testid={`doctor-bank-ifsc-${acc.id}`}
                                            className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:border-[#4ABDBD] outline-none" />
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <input type="text" value={acc.iban} onChange={(e) => updateAccount(acc.id, { iban: e.target.value })}
                                            placeholder={t('payments.iban', 'IBAN')} data-testid={`doctor-bank-iban-${acc.id}`}
                                            className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:border-[#4ABDBD] outline-none" />
                                        <input type="text" value={acc.bic} onChange={(e) => updateAccount(acc.id, { bic: e.target.value })}
                                            placeholder={t('payments.bic', 'BIC / SWIFT')} data-testid={`doctor-bank-bic-${acc.id}`}
                                            className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:border-[#4ABDBD] outline-none" />
                                    </div>
                                )}
                            </div>
                        ))}
                        <p className="text-xs text-gray-400">{t('payments.bank_hint', 'Used for payouts once online payments go live.')}</p>
                    </div>
                )}
            </div>

            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="doctor-payment-save"
                    className={`px-6 py-2.5 rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transition ${saving ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg'}`}
                >
                    {saving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
                </button>
            </div>
        </div>
    );
}
